<template>

  <VaLayout v-if="breakpoint.lgUp" class="h-screen bg-[var(--va-background-secondary)]">

    <template #left>

      <RouterLink
        class="bg-primary h-full flex items-center justify-center relative overflow-hidden"
        :style="[ { width: '35vw' }, { background: platformColor } ]"
        to="/"
        aria-label="Visit homepage"
      >

        <div class="absolute inset-0 particle-container" @mousemove="handleMouseMove" @mouseleave="handleMouseLeave">
          <canvas 

            ref="particleCanvas" 

            class="absolute inset-0 w-full h-full"

          ></canvas>

        </div>

        

        <VuesticLogo :height="28" start="#FFF" class="relative z-10" />

      </RouterLink>

    </template>

    <template #content>

      <main class="h-full flex items-center justify-center mx-auto max-w-[550px]">

        <RouterView />

      </main>

    </template>

  </VaLayout>



  <VaLayout v-else class="h-screen bg-[var(--va-background-secondary)]">

    <template #content>

      <div class="p-4">

        <main class="h-full flex items-center justify-center">

          <div class="flex flex-col items-center max-w-[550px] w-full">

            <RouterLink class="py-4 flex items-center justify-center" to="/" aria-label="Visit homepage">

              <VuesticLogo class="mt-6" start="#0E41C9" />

            </RouterLink>

            <RouterView />

          </div>

        </main>

      </div>

    </template>

  </VaLayout>

</template>



<script lang="ts" setup>

import { ref, reactive, onMounted, onUnmounted, nextTick } from 'vue'

import { useBreakpoint } from 'vuestic-ui'

import VuesticLogo from '../components/VuesticLogo.vue'
import { backgroundColor } from '../const'



const breakpoint = useBreakpoint()
const platformColor = backgroundColor

const particleCanvas = ref<HTMLCanvasElement | null>(null)

let animationId: number | null = null

let ctx: CanvasRenderingContext2D | null = null



interface Particle {

  x: number

  y: number

  baseX: number

  baseY: number

  vx: number

  vy: number

  radius: number

  opacity: number

  type: 'dust' | 'glow' | 'bubble'

  mass: number

  randomOffsetX: number

  randomOffsetY: number

  randomPhase: number

}



const mouse = reactive({

  x: -1000,

  y: -1000,

  isActive: false

})



const showConnections = ref(true)



const config = reactive({

  mouseRadius: 120,

  force: 0.8,

  friction: 0.92,

  springStrength: 0.02,

  particleCount: 60,

  randomMovement: {

    strength: 0.15,

    frequency: 0.002,

    damping: 0.98

  },

  connections: {

    maxDistance: 200,

    opacity: 0.25,

    width: 1

  }

})



const particles: Particle[] = []



const initParticles = () => {

  particles.length = 0

  

  for (let i = 0; i < config.particleCount; i++) {

    const type = i < 25 ? 'dust' : i < 45 ? 'bubble' : 'glow'

    const radius = type === 'dust' ? 1 + Math.random() * 2 : 

                   type === 'bubble' ? 3 + Math.random() * 5 : 

                   5 + Math.random() * 8

    

    particles.push({

      x: Math.random() * (particleCanvas.value?.width || 800),

      y: Math.random() * (particleCanvas.value?.height || 600),

      baseX: Math.random() * (particleCanvas.value?.width || 800),

      baseY: Math.random() * (particleCanvas.value?.height || 600),

      vx: 0,

      vy: 0,

      radius,

      opacity: type === 'dust' ? 0.5 + Math.random() * 0.3 : 

              type === 'bubble' ? 0.4 + Math.random() * 0.4 : 

              0.3 + Math.random() * 0.3,

      type,

      mass: radius / 10,

      randomOffsetX: Math.random() * Math.PI * 2,

      randomOffsetY: Math.random() * Math.PI * 2,

      randomPhase: Math.random() * Math.PI * 2

    })

  }

}



const animate = () => {

  if (!ctx || !particleCanvas.value) return

  

  ctx.clearRect(0, 0, particleCanvas.value.width, particleCanvas.value.height)

  

  drawConnections()

  

  particles.forEach(particle => {

    if (!ctx) return

    

    const dx = mouse.x - particle.x

    const dy = mouse.y - particle.y

    const distance = Math.sqrt(dx * dx + dy * dy)

    

    if (mouse.isActive && distance < config.mouseRadius) {

      const force = (1 - distance / config.mouseRadius) * config.force

      const angle = Math.atan2(dy, dx)

      

      particle.vx -= Math.cos(angle) * force * 2

      particle.vy -= Math.sin(angle) * force * 2

    }

    

    const springX = (particle.baseX - particle.x) * config.springStrength

    const springY = (particle.baseY - particle.y) * config.springStrength

    

    particle.vx += springX

    particle.vy += springY

    

    const time = Date.now() * config.randomMovement.frequency

    const randomX = Math.sin(time + particle.randomOffsetX) * config.randomMovement.strength

    const randomY = Math.cos(time + particle.randomOffsetY) * config.randomMovement.strength

    

    particle.vx += randomX

    particle.vy += randomY

    

    particle.vx *= config.friction

    particle.vy *= config.friction

    

    particle.vx *= config.randomMovement.damping

    particle.vy *= config.randomMovement.damping

    

    particle.x += particle.vx

    particle.y += particle.vy

    

    ctx.save()

    ctx.globalAlpha = particle.opacity

    

    if (particle.type === 'dust') {

      ctx.fillStyle = 'rgba(255, 255, 255, 0.6)'

      ctx.beginPath()

      ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2)

      ctx.fill()

    } else if (particle.type === 'bubble') {

      const gradient = ctx.createRadialGradient(

        particle.x - particle.radius * 0.3, 

        particle.y - particle.radius * 0.3, 

        0,

        particle.x, 

        particle.y, 

        particle.radius

      )

      gradient.addColorStop(0, 'rgba(255, 255, 255, 0.4)')

      gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.2)')

      gradient.addColorStop(1, 'rgba(255, 255, 255, 0.05)')

      

      ctx.fillStyle = gradient

      ctx.beginPath()

      ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2)

      ctx.fill()

      

      ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)'

      ctx.lineWidth = 1

      ctx.stroke()

    } else {

      const gradient = ctx.createRadialGradient(

        particle.x, 

        particle.y, 

        0,

        particle.x, 

        particle.y, 

        particle.radius * 1.5

      )

      gradient.addColorStop(0, 'rgba(255, 255, 255, 0.6)')

      gradient.addColorStop(0.3, 'rgba(255, 255, 255, 0.3)')

      gradient.addColorStop(0.6, 'rgba(255, 255, 255, 0.1)')

      gradient.addColorStop(1, 'rgba(255, 255, 255, 0)')

      

      ctx.fillStyle = gradient

      ctx.beginPath()

      ctx.arc(particle.x, particle.y, particle.radius * 1.5, 0, Math.PI * 2)

      ctx.fill()

      

      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)'

      ctx.beginPath()

      ctx.arc(particle.x, particle.y, particle.radius * 0.3, 0, Math.PI * 2)

      ctx.fill()

    }

    

    ctx.restore()

  })

  

  animationId = requestAnimationFrame(animate)

}



const handleMouseMove = (event: MouseEvent) => {

  const container = particleCanvas.value

  if (!container) return

  

  const rect = container.getBoundingClientRect()

  mouse.x = event.clientX - rect.left

  mouse.y = event.clientY - rect.top

  mouse.isActive = true

}



const handleMouseLeave = () => {

  mouse.isActive = false

}



const resizeCanvas = () => {

  if (!particleCanvas.value) return

  

  const container = particleCanvas.value.parentElement

  if (!container) return

  

  particleCanvas.value.width = container.clientWidth

  particleCanvas.value.height = container.clientHeight

  

  particles.forEach(particle => {

    particle.baseX = Math.random() * particleCanvas.value!.width

    particle.baseY = Math.random() * particleCanvas.value!.height

    particle.x = particle.baseX

    particle.y = particle.baseY

  })

}



const handleThemeChange = () => {

  initParticles()

}



const handleLanguageChange = () => {

  initParticles()

}



const toggleConnections = () => {

  showConnections.value = !showConnections.value

}



const drawConnections = () => {

  if (!ctx || !showConnections.value) return

  

  ctx.strokeStyle = `rgba(255, 255, 255, ${config.connections.opacity})`

  ctx.lineWidth = config.connections.width

  

  for (let i = 0; i < particles.length; i++) {

    for (let j = i + 1; j < particles.length; j++) {

      const p1 = particles[i]

      const p2 = particles[j]

      

      const dx = p1.x - p2.x

      const dy = p1.y - p2.y

      const distance = Math.sqrt(dx * dx + dy * dy)

      

      if (distance < config.connections.maxDistance) {

        const opacity = (1 - distance / config.connections.maxDistance) * config.connections.opacity

        ctx.strokeStyle = `rgba(255, 255, 255, ${opacity})`

        

        ctx.beginPath()

        ctx.moveTo(p1.x, p1.y)

        ctx.lineTo(p2.x, p2.y)

        ctx.stroke()

      }

    }

  }

}



onMounted(async () => {

  await nextTick()

  

  if (particleCanvas.value) {

    ctx = particleCanvas.value.getContext('2d')

    if (ctx) {

      resizeCanvas()

      initParticles()

      animate()

      

      window.addEventListener('resize', resizeCanvas)

      window.addEventListener('themeChanged', handleThemeChange)

      window.addEventListener('languageChanged', handleLanguageChange)

    }

  }

})



onUnmounted(() => {

  if (animationId) {

    cancelAnimationFrame(animationId)

  }

  window.removeEventListener('resize', resizeCanvas)

  window.removeEventListener('themeChanged', handleThemeChange)

  window.removeEventListener('languageChanged', handleLanguageChange)

})

</script>



<style scoped>

.particle-container {

  pointer-events: none;

}



.particle-container canvas {

  pointer-events: auto;

}

</style>